import { Link } from '@/components/primitives/Link';
import { tenantSelector } from '@/features/configuration/configurationSelector';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type { ProfileBookingType } from '@/types/profile';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { Button, Icons, Typography } from '@packages/webshop-design-system';
import { type FC, type PropsWithChildren } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';

const layoutConfig = {
  tickets: {
    icon: Icons.ticket,
    header: 'tickets',
    prefixUrl: '/account/tickets',
    menuItems: [
      ['upcoming', 'upcoming', <Icons.transportFrom className="h-6 w-6" />],
      ['history', 'history', <Icons.history className="h-6 w-6" />],
    ],
  },
  travelPasses: {
    icon: Icons.travelPass,
    header: 'travelPasses',
    prefixUrl: '/account/travel-passes',
    menuItems: [
      ['active', 'active', <Icons.transportFrom className="h-6 w-6" />],
      ['history', 'history', <Icons.history className="h-6 w-6" />],
    ],
  },
  vouchers: {
    icon: Icons.email,
    header: 'vouchers',
    prefixUrl: '/account/vouchers',
    menuItems: [
      ['active', 'active', <Icons.transportFrom className="h-6 w-6" />],
      ['history', 'history', <Icons.history className="h-6 w-6" />],
    ],
  },
  accountDetails: {
    icon: Icons.user,
    header: 'accountDetails',
    prefixUrl: '/account/details',
    menuItems: [
      ['myInformation', 'info', <Icons.user className="h-6 w-6" />],
      ['accountSettings', 'settings', <Icons.unlock className="h-6 w-6" />],
      ['loyalty', 'loyalty', <Icons.loyalty className="h-6 w-6" />],
    ],
  },
} as const;

interface HistoryLayoutProps extends PropsWithChildren {
  bookingType: ProfileBookingType;
}

const HistoryLayout: FC<HistoryLayoutProps> = ({
  bookingType,
  children,
}: HistoryLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMaas = useSelector(tenantSelector).value === 'maas';
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const {
    icon: Icon,
    header,
    prefixUrl,
    menuItems,
  } = layoutConfig[bookingType];

  const visibleMenuItems = isMaas
    ? menuItems.filter(([transKey]) => transKey !== 'loyalty')
    : menuItems;

  return (
    <div className="mx-auto flex w-full max-w-laptop flex-col gap-1 laptop:gap-6 laptop:px-4">
      <div className="flex items-center justify-between border-b border-neutral px-4 py-2 laptop:px-0 laptop:py-4">
        <div className="flex items-center gap-3">
          <Icon className="text-page h-6 w-6" />
          <Typography
            variant={isLaptopOrBigger ? 'heading1' : 'heading2'}
            as="h1"
          >
            <TransText i18nKey={header} />
          </Typography>
        </div>
        {/* Vouchers cannot be purchased in the webshop, so there is no
            "new" flow to start from that view (BR-51922). */}
        {(bookingType === 'tickets' || bookingType === 'travelPasses') && (
          <Button
            data-testid="profile-new-purchase"
            onClick={() => {
              const productType =
                bookingType === 'tickets' ? 'ticket' : 'travelPass';
              navigate(`/?productType=${productType}`);
            }}
            startAdornment={<Icons.addCircle className="h-5 w-5" />}
            className="w-fit"
          >
            <TransText i18nKey="new" />
          </Button>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-4 px-4 pb-6 pt-2 laptop:flex-row laptop:gap-16 laptop:p-0 laptop:pb-16">
        <div className="flex gap-6 rounded-xl border-b border-neutral-light bg-white p-4 laptop:mr-5 laptop:min-w-[160px] laptop:shrink-0 laptop:flex-col laptop:items-start laptop:gap-0 laptop:border-b-0 laptop:border-r">
          {visibleMenuItems.map(([transKey, url, Icon], key) => {
            const to = `${prefixUrl}/${url}`;
            const isActive = location.pathname === to;

            return (
              <Link
                key={key}
                to={to}
                className={cn(
                  'relative flex flex-1 flex-col justify-between gap-1 pb-3 pt-2 laptop:w-full laptop:flex-none laptop:flex-row laptop:gap-3 laptop:py-5',
                  { 'text-accent': isActive }
                )}
              >
                <div
                  className={cn(
                    'flex gap-2 laptop:flex-row laptop:items-start',
                    isLaptopOrBigger ? 'items-center' : 'flex-col items-center'
                  )}
                >
                  {Icon}
                  <Typography
                    variant={isLaptopOrBigger ? 'body1-bold' : 'body2-bold'}
                    className="text-center laptop:mr-5 laptop:text-left"
                  >
                    <TransText i18nKey={transKey} />
                  </Typography>
                </div>
              </Link>
            );
          })}
        </div>
        {children}
      </div>
    </div>
  );
};

export default HistoryLayout;
